/**
 * Role-Based Permission System
 *
 * Centralized utilities for determining user permissions and rendering
 * UI elements based on user roles in offices.
 */

import { OfficeWithRole } from './profileHelpers'

/**
 * Interface defining all possible permissions for office management
 */
export interface RolePermissions {
  /** Can edit office details (name, description, etc.) */
  canEditOffice: boolean
  /** Can create new desks in the office */
  canCreateDesks: boolean
  /** Can invite new users to the office */
  canInviteUsers: boolean
  /** Can manage office members and their roles */
  canManageMembers: boolean
  /** Can delete desks from the office */
  canDeleteDesks: boolean
  /** Can view office details and desks */
  canViewOffice: boolean
  /** Can book desks in the office */
  canBookDesks: boolean
}

/**
 * Gets all permissions for a user's role in an office
 *
 * @param userRole - The user's role in the office
 * @returns Complete permission set for the role
 */
export function getRolePermissions(
  userRole: 'owner' | 'manager' | 'member'
): RolePermissions {
  switch (userRole) {
    case 'owner':
      return {
        canEditOffice: true,
        canCreateDesks: true,
        canInviteUsers: true,
        canManageMembers: true,
        canDeleteDesks: true,
        canViewOffice: true,
        canBookDesks: true
      }
    case 'manager':
      return {
        canEditOffice: true,
        canCreateDesks: true,
        canInviteUsers: true,
        canManageMembers: false, // Managers cannot manage other managers
        canDeleteDesks: true,
        canViewOffice: true,
        canBookDesks: true
      }
    case 'member':
      return {
        canEditOffice: false,
        canCreateDesks: false,
        canInviteUsers: false,
        canManageMembers: false,
        canDeleteDesks: false,
        canViewOffice: true,
        canBookDesks: true
      }
    default:
      // Default to most restrictive permissions
      return {
        canEditOffice: false,
        canCreateDesks: false,
        canInviteUsers: false,
        canManageMembers: false,
        canDeleteDesks: false,
        canViewOffice: false,
        canBookDesks: false
      }
  }
}

/**
 * Checks if a user has a specific permission in an office
 *
 * @param office - Office with user role information
 * @param permission - The permission to check
 * @returns True if the user has the permission
 */
export function hasPermission(
  office: OfficeWithRole,
  permission: keyof RolePermissions
): boolean {
  const permissions = getRolePermissions(office.userRole)
  return permissions[permission]
}

/**
 * Checks if a user can manage an office (edit, create desks, invite)
 * This is a common check used throughout the application
 *
 * @param office - Office with user role information
 * @returns True if the user can manage the office
 */
export function canManageOffice(office: OfficeWithRole): boolean {
  return office.userRole === 'owner' || office.userRole === 'manager'
}

/**
 * Gets a human-readable description of what a role can do
 *
 * @param userRole - The role to describe
 * @returns Description of role capabilities
 */
export function getRoleCapabilityDescription(
  userRole: 'owner' | 'manager' | 'member'
): string {
  switch (userRole) {
    case 'owner':
      return 'Full access: Can manage office, desks, and all members'
    case 'manager':
      return 'Management access: Can edit office, create desks, and invite members'
    case 'member':
      return 'View access: Can view office details and book desks'
    default:
      return 'No access'
  }
}

/**
 * Gets the appropriate color for role display based on permission level
 *
 * @param userRole - The role to get color for
 * @returns Material-UI color name for the role
 */
export function getRoleColor(
  userRole: 'owner' | 'manager' | 'member'
): 'primary' | 'secondary' | 'default' {
  switch (userRole) {
    case 'owner':
      return 'primary'
    case 'manager':
      return 'secondary'
    case 'member':
      return 'default'
    default:
      return 'default'
  }
}

/**
 * Determines which action buttons should be visible for a user's role
 *
 * @param office - Office with user role information
 * @returns Object indicating which buttons should be shown
 */
export interface ActionButtonVisibility {
  showViewButton: boolean
  showEditButton: boolean
  showAddDeskButton: boolean
  showInviteButton: boolean
  showManageMembersButton: boolean
}

export function getActionButtonVisibility(
  office: OfficeWithRole
): ActionButtonVisibility {
  const permissions = getRolePermissions(office.userRole)

  return {
    showViewButton: permissions.canViewOffice,
    showEditButton: permissions.canEditOffice,
    showAddDeskButton: permissions.canCreateDesks,
    showInviteButton: permissions.canInviteUsers,
    showManageMembersButton: permissions.canManageMembers
  }
}

/**
 * Gets appropriate button variant based on action importance and user role
 *
 * @param action - The action type
 * @param userRole - The user's role
 * @returns Material-UI button variant
 */
export function getButtonVariant(
  action: 'view' | 'edit' | 'create' | 'invite' | 'manage',
  userRole: 'owner' | 'manager' | 'member'
): 'text' | 'outlined' | 'contained' {
  // Members only get outlined buttons for limited actions
  if (userRole === 'member') {
    return action === 'view' ? 'outlined' : 'text'
  }

  // Managers and owners get more prominent buttons
  switch (action) {
    case 'view':
      return 'outlined'
    case 'edit':
      return 'outlined'
    case 'create':
      return 'contained'
    case 'invite':
      return 'contained'
    case 'manage':
      return 'outlined'
    default:
      return 'outlined'
  }
}
